import { Component, cloneElement, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import * as Sentry from '@sentry/react';

import { JotaiContext } from '../jotai_provider';

import { maybeCurrentAccountLoadable } from '../store';

import { PROD } from '../env';

import type { Account } from '@rewardopl/types';
import type { Location } from 'react-router-dom';

const isProduction = PROD;

type ErrorBoundaryProps = {
  children: React.ReactNode;
  currentAccount: Account | null;
  fallback?: React.ReactElement<{ resetError?: () => void }> | null;
  location: Location;
  resetStore: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };

    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    if (error instanceof Error && error.message === 'Forbidden') {
      const { currentAccount } = this.props;

      if (currentAccount) {
        localStorage.removeItem('account');
        localStorage.removeItem('account_role');
        localStorage.removeItem('business');
        window.location.href = '/login';
      }
      return;
    }

    localStorage.clear();

    if (isProduction) {
      Sentry.captureException(error);
    }

    this.setState({ hasError: true });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { location } = this.props;
    const { location: prevLocation } = prevProps;

    if (prevLocation.pathname !== location.pathname) {
      this.setState({ hasError: false });
    }
  }

  resetError() {
    const { resetStore } = this.props;

    resetStore();

    this.setState({ hasError: false });
  }

  render() {
    const { resetError } = this;
    const { children, fallback } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return fallback ? cloneElement(fallback, { resetError }) : null;
    }

    return children;
  }
}

function withCurrentAccount<P extends { currentAccount: Account | null }>(
  InternalComponent: React.ComponentType<P>,
) {
  function WithCurrentAccountInternal(props: Omit<P, 'currentAccount'>) {
    const currentAccountLoadable = useAtomValue(maybeCurrentAccountLoadable);

    return (
      <InternalComponent
        {...(props as P)}
        currentAccount={
          (currentAccountLoadable.state === 'hasData' && currentAccountLoadable.data) || null
        }
      />
    );
  }

  WithCurrentAccountInternal.displayName = `withCurrentAccount(${InternalComponent.name})`;

  return WithCurrentAccountInternal;
}

function withLocation<P extends { location: Location }>(InternalComponent: React.ComponentType<P>) {
  function WithLocationInternal(props: Omit<P, 'location'>) {
    const location = useLocation();

    return <InternalComponent {...(props as P)} location={location} />;
  }

  WithLocationInternal.displayName = `withLocation(${InternalComponent.name})`;

  return WithLocationInternal;
}

function withJotai<P extends { resetStore: () => void }>(
  InternalComponent: React.ComponentType<P>,
) {
  function WithJotaiInternal(props: Omit<P, 'resetStore'>) {
    const jotaiContext = useContext(JotaiContext);

    if (!jotaiContext) {
      throw new Error('Jotai context not found');
    }

    const { resetStore } = jotaiContext;

    return <InternalComponent {...(props as P)} resetStore={resetStore} />;
  }

  WithJotaiInternal.displayName = `withJotai(${InternalComponent.name})`;

  return WithJotaiInternal;
}

export default withJotai(withLocation(withCurrentAccount(ErrorBoundary)));
